// "Run preSave on…" (spec/dashboard.md sections 10.6 and 10.8): the hooks run against the current drafts or one document,
// nothing is saved; the report lists, per entity and per extension, the blocks, the warnings and the time taken. "on the
// server" sends the drafts as a `documents:batch` dry run and lists the server's verdict per entity (its validators too).
import type { ErrorDetail } from '../../api/types';
import type { ChangedEntity, PreSaveOutcome } from '../../extensions/protocol';
import { Badge, type Tone } from '../../ui/Chip';
import { Modal } from '../../ui/Modal';
import { EmptyState } from '../../ui/Banner';
import { Shield } from '../../ui/icons';

export interface PreSaveRun {
  /** what the run was over: "the current drafts" or the document's label */
  subject: string;
  entities: ChangedEntity[];
  outcomes: PreSaveOutcome[];
  /** the label of an entity by its id */
  labels: Record<string, string>;
}

const WORD: Record<PreSaveOutcome['result']['kind'], { word: string; tone: Tone }> = {
  allow: { word: 'allowed', tone: 'success' },
  block: { word: 'blocked', tone: 'danger' },
  failed: { word: 'failed — blocks the save', tone: 'danger' },
  timeout: { word: 'timed out — blocks the save', tone: 'danger' },
};

/** The entities an outcome blocks: those it names (by id or unnyId), every entity when it names none; a throw or a timeout blocks every entity (fail-closed). */
function blockedBy(outcome: PreSaveOutcome, entities: ChangedEntity[]): ChangedEntity[] {
  if (outcome.result.kind === 'failed' || outcome.result.kind === 'timeout') return entities;
  if (outcome.result.kind !== 'block') return [];
  const named = outcome.result.entities;
  if (named.length === 0) return entities;
  return entities.filter((e) => named.includes(e.id) || named.includes(e.unnyId));
}

function detailOf(o: PreSaveOutcome): string {
  if (o.result.kind === 'block') return o.result.reason;
  if (o.result.kind === 'failed') return `Validator ${o.extension} failed: ${o.result.error}`;
  if (o.result.kind === 'timeout') return `Validator ${o.extension} timed out (5 s)`;
  return '';
}

export function PreSaveReport({ run, onClose }: { run: PreSaveRun; onClose: () => void }) {
  const { entities, outcomes, labels } = run;
  const label = (e: ChangedEntity) => labels[e.id] ?? `${e.templateName.split('.').pop()} ${e.unnyId}`;
  return (
    <Modal title="preSave report" open size="l" onClose={onClose}>
      <div data-testid="presave-report" className="presave-report">
        <div className="stat-strip presave-stats">
          <div className="stat">
            <span className="stat-label">Entities</span>
            <span className="stat-value">{entities.length}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Extensions</span>
            <span className="stat-value">{outcomes.length}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Blocking</span>
            <span className="stat-value">{outcomes.filter((o) => o.result.kind !== 'allow').length}</span>
          </div>
        </div>
        <p className="muted small presave-lede">
          preSave on {run.subject}: {entities.length} {entities.length === 1 ? 'entity' : 'entities'}, {outcomes.length} {outcomes.length === 1 ? 'extension' : 'extensions'} — nothing was saved.
        </p>
        {outcomes.length === 0 ? (
          <EmptyState icon={<Shield />} title="No preSave hooks" body="No loaded extension has a preSave hook." />
        ) : (
          <>
            <h3>By extension</h3>
            <div className="scripts-table-wrap">
            <table className="grid presave-table">
              <thead>
                <tr>
                  <th scope="col">Extension</th>
                  <th scope="col">Result</th>
                  <th scope="col">Reason</th>
                  <th scope="col">Warnings</th>
                  <th scope="col">Time</th>
                </tr>
              </thead>
              <tbody>
                {outcomes.map((o) => (
                  <tr key={o.extension} data-extension={o.extension} className="grid-row">
                    <th scope="row">{o.extension}</th>
                    <td>
                      <Badge tone={WORD[o.result.kind].tone}>{WORD[o.result.kind].word}</Badge>
                      {o.result.kind === 'block' && (
                        <div className="muted small">{o.result.entities.length === 0 ? 'every changed entity' : blockedBy(o, entities).map(label).join(', ') || o.result.entities.join(', ')}</div>
                      )}
                    </td>
                    <td className="presave-reason">{detailOf(o)}</td>
                    <td>
                      {o.warnings.length === 0 ? (
                        <span className="muted">—</span>
                      ) : (
                        <ul className="presave-warnings">
                          {o.warnings.map((w, i) => (
                            <li key={i}>{w}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="mono">{Math.round(o.ms)} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {entities.length > 0 && (
              <>
                <h3>By entity</h3>
                <ul className="presave-entities">
                  {entities.map((e) => {
                    const blocks = outcomes.filter((o) => blockedBy(o, [e]).length > 0);
                    return (
                      <li key={e.id} data-entity={e.id}>
                        <strong>{label(e)}</strong> <Badge tone={blocks.length ? 'danger' : 'success'}>{blocks.length ? 'blocked' : 'allowed'}</Badge>
                        {blocks.map((o) => (
                          <div key={o.extension} className="presave-reason small">
                            {o.extension}: {detailOf(o)}
                          </div>
                        ))}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

// ---- on the server -------------------------------------------------------------------------------------------------------

/** The server's verdict on one entity of the dry run. */
export interface ServerVerdict {
  id: string;
  label: string;
  /** allowed: the dry run's "would apply" (status `skipped` without an error); blocked: a validator refused it; failed: anything else (schema, version, …) */
  verdict: 'allowed' | 'blocked' | 'failed';
  /** the lines shown under the entity: the validators' messages (each starts with the extension's name), else the error's details or message */
  messages: string[];
}

export interface ServerPreSaveRun {
  subject: string;
  verdicts: ServerVerdict[];
}

const EXTENSION_ISSUES = new Set(['extension_blocked', 'extension_failed']);

type VerdictRow = { status: string; error: null | { message: string; details: null | Pick<ErrorDetail, 'message' | 'issue'>[] } };

/** One result row of the dry run as a verdict. */
export function verdictOf(id: string, label: string, row: VerdictRow): ServerVerdict {
  if (!row.error && (row.status === 'skipped' || row.status === 'applied')) return { id, label, verdict: 'allowed', messages: [] };
  const details = row.error?.details ?? [];
  const extension = details.filter((d) => !!d.issue && EXTENSION_ISSUES.has(d.issue));
  if (extension.length > 0) return { id, label, verdict: 'blocked', messages: extension.map((d) => d.message) };
  const lines = details.map((d) => d.message).filter((m) => !!m);
  return { id, label, verdict: 'failed', messages: lines.length > 0 ? lines : [row.error?.message ?? `The server answered ${row.status}.`] };
}

const VERDICT: Record<ServerVerdict['verdict'], { word: string; tone: Tone }> = {
  allowed: { word: 'allowed', tone: 'success' },
  blocked: { word: 'blocked by a validator', tone: 'danger' },
  failed: { word: 'failed', tone: 'danger' },
};

export function ServerPreSaveReport({ run, onClose }: { run: ServerPreSaveRun; onClose: () => void }) {
  const { verdicts } = run;
  return (
    <Modal title="preSave on the server" open size="l" onClose={onClose}>
      <div data-testid="server-presave-report" className="presave-report">
        <p className="muted small">
          A dry run of {run.subject} on the server ({verdicts.length} {verdicts.length === 1 ? 'entity' : 'entities'}): the schema and the server-side validators ran — nothing was saved.
        </p>
        {verdicts.length === 0 ? (
          <p>No document drafts to check.</p>
        ) : (
          <ul className="presave-entities">
            {verdicts.map((v) => (
              <li key={v.id} data-entity={v.id}>
                <strong>{v.label}</strong> <Badge tone={VERDICT[v.verdict].tone}>{VERDICT[v.verdict].word}</Badge>
                {v.messages.map((m, i) => (
                  <div key={i} className="presave-reason small">
                    {m}
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
